import React, { useState, useEffect, useRef } from "react";
import {
  Stack,
  Divider,
  Button,
  Checkbox,
  Box,
  Paper,
} from "@vapor/react-material";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import VaporToolbar from "@vapor/react-custom/VaporToolbar";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@vapor/react-x-date-pickers/DatePicker";
import { LocalizationProvider } from "@vapor/react-x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@vapor/react-x-date-pickers/AdapterDateFns";
import { it } from "date-fns/locale";
import { useSnackbar } from "notistack";
import sharedDatas from "../../businessLogic/sharedDatas";
import { bsaRischio } from "../../businessLogic/constants/securityAreas";
import { useAreaPermissions } from "../../businessLogic/hooks/useAreaPermissions";
import { getRiskGradeOffset } from "../../businessLogic/helpers/riskGradeHelpers";

// Import dei sottocomponenti
import { RischioSectionA } from "./RischioSectionA";
import { RischioSectionB } from "./RischioSectionB";
import { RischioSectionC } from "./RischioSectionC";
import { RischioSummary } from "./RischioSummary";

import {
  SaveOutlined as SaveIcon,
  CloseOutlined as CancelIcon,
} from "@mui/icons-material";

// Import degli stili
import {
  chipGreen,
  chipYellow,
  chipOrange,
  chipRed,
} from "../../businessLogic/styles";

export const RischioDetail = ({ 
  selectedRischio, 
  selectedPrestazione, 
  dataValutazione,
  onSaveRischio, 
  onCancel, 
  getAdeguateVerificationRules,
  baseRischioGradoList = [],
  rischioPaeseCliente,
  rischioProvinciaCliente,
  rischioPaesePrest,
  rischioProvinciaPrest,
  fascicoloHasTitEfPep = false
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  //#region State Management
  const tenantTypeId = sharedDatas.getPropertyByName("userModel").tenantTipoId;
  const { permissions, getPermissions } = useAreaPermissions(bsaRischio);
  const isUserAllowedToManage = selectedRischio?.id === -1 ? permissions.canCreate : permissions.canUpdate;
  
  // Slider values
  const [sliderVal, setSliderVal] = useState({
    sliderA1: 1,
    sliderA2: 1,
    sliderA3: 1,
    sliderA4: 1,
    sliderB1: 1,
    sliderB2: 1,
    sliderB3: 1,
    sliderB4: 1,
    sliderB5: 1,
    sliderB6: 1,
    sliderB7: 1,
    sliderC1: 1,
    sliderC2: 1,
    sliderC3: 1,
  });

  // Checkbox values
  const [chbVal, setChbVal] = useState({
    chbA1_1: false,
    chbA1_2: false,
    chbA1_3: false,
    chbA1_4: false,
    chbA1_5: false,
    chbA1_6: false,
    chbA1_7: false,
    chbA1_Altro: false,
    chbA2_1: false,
    chbA2_2: false,
    chbA2_3: false,
    chbA2_Altro: false,
    chbA3_1: false,
    chbA3_2: false,
    chbA3_3: false,
    chbA3_4: false,
    chbA3_Altro: false,
    chbA4_1: false,
    chbA4_2: false,
    chbA4_3: false,
    chbA4_4: false,
    chbA4_Altro: false,
    chbB1_1: false,
    chbB1_2: false,
    chbB1_3: false,
    chbB1_4: false,
    chbB1_Altro: false,
    chbB2_1: false,
    chbB2_2: false,
    chbB2_3: false,
    chbB2_4: false,
    chbB2_Altro: false,
    chbB3_1: false,
    chbB3_2: false,
    chbB3_3: false,
    chbB3_Altro: false,
    chbB4_1: false,
    chbB4_2: false,
    chbB4_3: false,
    chbB4_Altro: false,
    chbB5_1: false,
    chbB5_2: false,
    chbB5_3: false,
    chbB5_4: false,
    chbB5_Altro: false,
    chbB6_1: false,
    chbB6_2: false,
    chbB6_3: false,
    chbB6_4: false,
    chbB6_Altro: false,
    chbTabellaB: true,
    chbHasTitEffPep: false,
  });

  // Radio button values for tenant type 3
  const [selectedRadioValues, setSelectedRadioValues] = useState({
    A1: "",
    A2: "",
    A3: "",
    A4: "",
    B1: "",
    B2: "",
    B3: "",
    B4: "",
    B5: "",
    B6: "",
  });

  // Calculation results
  const [subTotaleA, setSubTotaleA] = useState(6);
  const [subTotaleB, setSubTotaleB] = useState(6);
  const [subTotaleC, setSubTotaleC] = useState(3);
  
  // Risk levels
  const [rischioInerenteLivelloS, setRischioInerenteLivelloS] = useState("Non significativo");
  const [rischioInerenteLivelloC, setRischioInerenteLivelloC] = useState(chipGreen);
  const [rischioEffettivoLivelloS, setRischioEffettivoLivelloS] = useState("Non significativo");
  const [rischioEffettivoLivelloC, setRischioEffettivoLivelloC] = useState(chipGreen);
  const [rischioSpecificoLivelloS, setRischioSpecificoLivelloS] = useState("Non significativo");
  const [rischioSpecificoLivelloC, setRischioSpecificoLivelloC] = useState(chipGreen);
  
  // Adeguata verifica states
  const [adeguataVerifica, setAdeguataVerifica] = useState(1);
  const [adeguataVerificaS, setAdeguataVerificaS] = useState(t("valutazione.form.adeguataVerifica1"));
  const [adeguataVerificaC, setAdeguataVerificaC] = useState(chipGreen);
  const [rules,setRules] = useState(null);
  
  // Totals
  const [totRischioInerente, setTotRischioInerente] = useState(0);
  const [totRischioSpecifico, setTotRischioSpecifico] = useState(0);
  const [totRischioSpecificoPonderato, setTotRischioSpecificoPonderato] = useState(0);
  const [totRischioEffettivo, setTotRischioEffettivo] = useState(0);

  // Date controls
  const [dataContCostAdeguataVerifica, setDataContCostAdeguataVerifica] = useState(null);
  const [offsetMesi, setOffsetMesi] = useState(null);
  const previousControlDateInputs = useRef(null);
  const riskInputsChanged = useRef(false);

  // Text notes for "Altro" fields
  const [textNotes, setTextNotes] = useState({
    txtAltroA1: "",
    txtAltroA2: "",
    txtAltroA3: "",
    txtAltroA4: "",
    txtAltroB1: "",
    txtAltroB2: "",
    txtAltroB3: "",
    txtAltroB4: "",
    txtAltroB5: "",
    txtAltroB6: "",
  });
  //#endregion

  //#region Slider Configuration
  const sliderLivelli = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
  ];

  const sliderLivelliAvv = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
  ];
  //#endregion

  //#region Effect for loading rischio data
  useEffect(() => {
    if (selectedRischio && (selectedRischio.id > 0 || selectedRischio.id === -1)) {
      if (previousControlDateInputs.current?.riskId !== selectedRischio.id) {
        riskInputsChanged.current = false;
      }
      // Carica i dati del rischio selezionato (esistente o nuovo)
      setSliderVal({
        sliderA1: selectedRischio.sp_a1_livello || 1,
        sliderA2: selectedRischio.sp_a2_livello || 1,
        sliderA3: selectedRischio.sp_a3_livello || 1,
        sliderA4: selectedRischio.sp_a4_livello || 1,
        sliderB1: selectedRischio.sp_b1_livello || 1,
        sliderB2: selectedRischio.sp_b2_livello || 1,
        sliderB3: selectedRischio.sp_b3_livello || 1,
        sliderB4: selectedRischio.sp_b4_livello || 1,
        sliderB5: selectedRischio.sp_b5_livello || 1,
        sliderB6: selectedRischio.sp_b6_livello || 1,
        sliderB7: selectedRischio.sp_b7_livello || 1,
        sliderC1: selectedRischio.sp_c1_1_livello || 1,
        sliderC2: selectedRischio.sp_c1_2_livello || 1,
        sliderC3: selectedRischio.sp_c1_3_livello || 1,
      });

      setChbVal({
        chbA1_1: selectedRischio.sp_a1_1 || false,
        chbA1_2: selectedRischio.sp_a1_2 || false,
        chbA1_3: selectedRischio.sp_a1_3 || false,
        chbA1_4: selectedRischio.sp_a1_4 || false,
        chbA1_5: selectedRischio.sp_a1_5 || false,
        chbA1_6: selectedRischio.sp_a1_6 || false,
        chbA1_7: selectedRischio.sp_a1_7 || false,
        chbA1_Altro: selectedRischio.sp_a1_altro || false,
        chbA2_1: selectedRischio.sp_a2_1 || false,
        chbA2_2: selectedRischio.sp_a2_2 || false,
        chbA2_3: selectedRischio.sp_a2_3 || false,
        chbA2_Altro: selectedRischio.sp_a2_altro || false,
        chbA3_1: selectedRischio.sp_a3_1 || false,
        chbA3_2: selectedRischio.sp_a3_2 || false,
        chbA3_3: selectedRischio.sp_a3_3 || false,
        chbA3_4: selectedRischio.sp_a3_4 || false,
        chbA3_Altro: selectedRischio.sp_a3_altro || false,
        chbA4_1: selectedRischio.sp_a4_1 || false,
        chbA4_2: selectedRischio.sp_a4_2 || false,
        chbA4_3: selectedRischio.sp_a4_3 || false,
        chbA4_4: selectedRischio.sp_a4_4 || false,
        chbA4_Altro: selectedRischio.sp_a4_altro || false,
        chbB1_1: selectedRischio.sp_b1_1 || false,
        chbB1_2: selectedRischio.sp_b1_2 || false,
        chbB1_3: selectedRischio.sp_b1_3 || false,
        chbB1_4: selectedRischio.sp_b1_4 || false,
        chbB1_Altro: selectedRischio.sp_b1_altro || false,
        chbB2_1: selectedRischio.sp_b2_1 || false,
        chbB2_2: selectedRischio.sp_b2_2 || false,
        chbB2_3: selectedRischio.sp_b2_3 || false,
        chbB2_4: selectedRischio.sp_b2_4 || false,
        chbB2_Altro: selectedRischio.sp_b2_altro || false,
        chbB3_1: selectedRischio.sp_b3_1 || false,
        chbB3_2: selectedRischio.sp_b3_2 || false,
        chbB3_3: selectedRischio.sp_b3_3 || false,
        chbB3_Altro: selectedRischio.sp_b3_altro || false,
        chbB4_1: selectedRischio.sp_b4_1 || false,
        chbB4_2: selectedRischio.sp_b4_2 || false,
        chbB4_3: selectedRischio.sp_b4_3 || false,
        chbB4_Altro: selectedRischio.sp_b4_altro || false,
        chbB5_1: selectedRischio.sp_b5_1 || false,
        chbB5_2: selectedRischio.sp_b5_2 || false,
        chbB5_3: selectedRischio.sp_b5_3 || false,
        chbB5_4: selectedRischio.sp_b5_4 || false,
        chbB5_Altro: selectedRischio.sp_b5_altro || false,
        chbB6_1: selectedRischio.sp_b6_1 || false,
        chbB6_2: selectedRischio.sp_b6_2 || false,
        chbB6_3: selectedRischio.sp_b6_3 || false,
        chbB6_4: selectedRischio.sp_b6_4 || false,
        chbB6_Altro: selectedRischio.sp_b6_altro || false,
        chbTabellaB: selectedRischio.hasTabellaB !== undefined ? selectedRischio.hasTabellaB : true,
        chbHasTitEffPep: selectedRischio.hasTitEffPep || false,
      });

      // Carica le note di testo per i campi "Altro"
      setTextNotes({
        txtAltroA1: selectedRischio.sp_a1_altro_note || "",
        txtAltroA2: selectedRischio.sp_a2_altro_note || "",
        txtAltroA3: selectedRischio.sp_a3_altro_note || "",
        txtAltroA4: selectedRischio.sp_a4_altro_note || "",
        txtAltroB1: selectedRischio.sp_b1_altro_note || "",
        txtAltroB2: selectedRischio.sp_b2_altro_note || "",
        txtAltroB3: selectedRischio.sp_b3_altro_note || "",
        txtAltroB4: selectedRischio.sp_b4_altro_note || "",
        txtAltroB5: selectedRischio.sp_b5_altro_note || "",
        txtAltroB6: selectedRischio.sp_b6_altro_note || "",
      });

      // Inizializza la data di controllo
      if (selectedRischio.id !== -1 && selectedRischio.dataControlloCostante) {
        setDataContCostAdeguataVerifica(selectedRischio.dataControlloCostante);
      } else {
        // Senza una data salvata, attendi l'offset del grado prima di calcolarla.
        setDataContCostAdeguataVerifica(null);
      }

      // Per gli avvocati, carica i valori dei radio button dai checkbox salvati
      if (tenantTypeId === 3) {
        const radioValues = {};
        
        // Mappatura diretta tra checkbox e campi database
        const checkboxToDbMap = {
          'chbA1_1': 'sp_a1_1', 'chbA1_2': 'sp_a1_2', 'chbA1_3': 'sp_a1_3', 'chbA1_4': 'sp_a1_4', 'chbA1_5': 'sp_a1_5', 'chbA1_6': 'sp_a1_6', 'chbA1_7': 'sp_a1_7',
          'chbA2_1': 'sp_a2_1', 'chbA2_2': 'sp_a2_2',
          'chbA3_1': 'sp_a3_1', 'chbA3_2': 'sp_a3_2',
          'chbA4_1': 'sp_a4_1', 'chbA4_2': 'sp_a4_2', 'chbA4_3': 'sp_a4_3', 'chbA4_4': 'sp_a4_4',
          'chbB1_1': 'sp_b1_1', 'chbB1_2': 'sp_b1_2', 'chbB1_3': 'sp_b1_3', 'chbB1_4': 'sp_b1_4',
          'chbB2_1': 'sp_b2_1', 'chbB2_2': 'sp_b2_2', 'chbB2_3': 'sp_b2_3', 'chbB2_4': 'sp_b2_4',
          'chbB3_1': 'sp_b3_1', 'chbB3_2': 'sp_b3_2',
          'chbB4_1': 'sp_b4_1', 'chbB4_2': 'sp_b4_2',
          'chbB5_1': 'sp_b5_1', 'chbB5_2': 'sp_b5_2', 'chbB5_3': 'sp_b5_3', 'chbB5_4': 'sp_b5_4',
          'chbB6_1': 'sp_b6_1', 'chbB6_2': 'sp_b6_2', 'chbB6_3': 'sp_b6_3', 'chbB6_4': 'sp_b6_4',
        };

        // Sezione A - trova quale radio button è selezionato per ogni sezione
        const sectionMap = {
          A1: ['chbA1_1', 'chbA1_2', 'chbA1_3', 'chbA1_4', 'chbA1_5', 'chbA1_6', 'chbA1_7'],
          A2: ['chbA2_1', 'chbA2_2'],
          A3: ['chbA3_1', 'chbA3_2'],
          A4: ['chbA4_1', 'chbA4_2', 'chbA4_3', 'chbA4_4'],
          B1: ['chbB1_1', 'chbB1_2', 'chbB1_3', 'chbB1_4'],
          B2: ['chbB2_1', 'chbB2_2', 'chbB2_3', 'chbB2_4'],
          B3: ['chbB3_1', 'chbB3_2'],
          B4: ['chbB4_1', 'chbB4_2'],
          B5: ['chbB5_1', 'chbB5_2', 'chbB5_3', 'chbB5_4'],
          B6: ['chbB6_1', 'chbB6_2', 'chbB6_3', 'chbB6_4'],
        };

        Object.keys(sectionMap).forEach(sectionId => {
          const checkboxIds = sectionMap[sectionId];
          for (let i = 0; i < checkboxIds.length; i++) {
            const checkboxId = checkboxIds[i];
            const dbField = checkboxToDbMap[checkboxId];
            if (dbField && selectedRischio[dbField]) {
              radioValues[sectionId] = checkboxId;
              break; // Solo un radio button per sezione
            }
          }
        });

        setSelectedRadioValues(radioValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRischio]);

  //#endregion

  //#region Event Handlers
  const handleSliderChange = (event, value) => {
    riskInputsChanged.current = true;
    const name = event.target.name;
    setSliderVal(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (event) => {
    const { id, checked, value, type } = event.target;
    
    if (type === 'text' || id.startsWith("txt")) {
      // Gestisci i campi di testo - l'ID è già nel formato corretto txtAltroA1, txtAltroB2, etc.
      setTextNotes(prev => ({ ...prev, [id]: value }));
      return;
    }
    riskInputsChanged.current = true;
    setChbVal(prev => ({ ...prev, [id]: checked }));
  };

  const handleRadioChange = (radioId, sectionId) => {
    riskInputsChanged.current = true;
    setSelectedRadioValues(prev => ({ ...prev, [sectionId]: radioId }));
    
    // Per gli avvocati, sincronizza i radio button con i checkbox
    if (tenantTypeId === 3) {
      // Resetta tutti i checkbox della sezione
      const sectionMap = {
        A1: ['chbA1_1', 'chbA1_2', 'chbA1_3', 'chbA1_4', 'chbA1_5', 'chbA1_6', 'chbA1_7'],
        A2: ['chbA2_1', 'chbA2_2'],
        A3: ['chbA3_1', 'chbA3_2'],
        A4: ['chbA4_1', 'chbA4_2', 'chbA4_3', 'chbA4_4'],
        B1: ['chbB1_1', 'chbB1_2', 'chbB1_3'],
        B2: ['chbB2_1', 'chbB2_2', 'chbB2_3', 'chbB2_4'],
        B3: ['chbB3_1', 'chbB3_2'],
        B4: ['chbB4_1', 'chbB4_2'],
        B5: ['chbB5_1', 'chbB5_2', 'chbB5_3', 'chbB5_4'],
        B6: ['chbB6_1', 'chbB6_2', 'chbB6_3', 'chbB6_4'],
      };

      if (sectionMap[sectionId]) {
        setChbVal(prev => {
          const updated = { ...prev };
          // Resetta tutti i checkbox della sezione
          sectionMap[sectionId].forEach(checkboxId => {
            updated[checkboxId] = false;
          });
          // Imposta il checkbox selezionato
          updated[radioId] = true;
          return updated;
        });
      }
    }
  };

  const handleTableRowClick = (checkboxId) => {
    riskInputsChanged.current = true;
    setChbVal(prev => ({ ...prev, [checkboxId]: !prev[checkboxId] }));
  };

  const handleDateChange = (date) => {
    setDataContCostAdeguataVerifica(date);
  };

  const handleSaveRischio = async () => {
    if (!selectedRischio) return;
    const currentPermissions = getPermissions();
    if (!(selectedRischio.id === -1 ? currentPermissions.canCreate : currentPermissions.canUpdate)) return;
    if (!onSaveRischio) {
      console.warn("onSaveRischio function not provided");
      return;
    }

    if (offsetMesi === null || !dataContCostAdeguataVerifica) {
      enqueueSnackbar("Offset del rischio non disponibile per il tipo di tenant corrente", { variant: "error" });
      return;
    }

    // Validazione: ogni sezione visibile deve avere almeno una voce selezionata
    const validationErrors = [];

    // Validazione Sezione A (sempre visibile)
    const sectionsA = [
      { id: 'A1', checkboxes: ['chbA1_1', 'chbA1_2', 'chbA1_3', 'chbA1_4', 'chbA1_5', 'chbA1_6', 'chbA1_7', 'chbA1_Altro'], radioSection: 'A1' },
      { id: 'A2', checkboxes: ['chbA2_1', 'chbA2_2', 'chbA2_3', 'chbA2_Altro'], radioSection: 'A2' },
      { id: 'A3', checkboxes: ['chbA3_1', 'chbA3_2', 'chbA3_3', 'chbA3_4', 'chbA3_Altro'], radioSection: 'A3' },
      { id: 'A4', checkboxes: ['chbA4_1', 'chbA4_2', 'chbA4_3', 'chbA4_4', 'chbA4_Altro'], radioSection: 'A4' }
    ];

    sectionsA.forEach(section => {
      if (tenantTypeId === 3) {
        // Per avvocati: verifica se c'è un radio button selezionato
        if (!selectedRadioValues[section.radioSection]) {
          validationErrors.push(`${section.id}`);
        }
      } else {
        // Per altri tenant: verifica se c'è almeno un checkbox selezionato
        if(tenantTypeId !== 2){ // Escludi tenantTypeId 2 (Consulenti del lavoro)
          const hasSelection = section.checkboxes.some(checkbox => chbVal[checkbox]);
          if (!hasSelection) {
            validationErrors.push(`${section.id}`);
          }
        }
      }
    });

    // Validazione Sezione B (solo se attiva)
    if (chbVal.chbTabellaB && tenantTypeId !== 2) { // Escludi tenantTypeId 2 (Consulenti del lavoro)
      const sectionsB = [
        { id: 'B1', checkboxes: ['chbB1_1', 'chbB1_2', 'chbB1_3', 'chbB1_4', 'chbB1_Altro'] },
        { id: 'B2', checkboxes: ['chbB2_1', 'chbB2_2', 'chbB2_3', 'chbB2_4', 'chbB2_Altro'] },
        { id: 'B3', checkboxes: ['chbB3_1', 'chbB3_2', 'chbB3_3', 'chbB3_Altro'] },
        { id: 'B4', checkboxes: ['chbB4_1', 'chbB4_2', 'chbB4_3', 'chbB4_Altro'] },
        { id: 'B5', checkboxes: ['chbB5_1', 'chbB5_2', 'chbB5_3', 'chbB5_4', 'chbB5_Altro'] },
        { id: 'B6', checkboxes: ['chbB6_1', 'chbB6_2', 'chbB6_3', 'chbB6_4', 'chbB6_Altro'] }
      ];

      sectionsB.forEach(section => {
        const hasSelection = section.checkboxes.some(checkbox => chbVal[checkbox]);
        if (!hasSelection) {
          validationErrors.push(`${section.id}`);
        }
      });
    }

    // Se ci sono errori di validazione, mostra un messaggio e interrompi il salvataggio
    if (validationErrors.length > 0){
      const errorMessage = validationErrors.length === 1 
        ? `La sezione ${validationErrors[0]} deve essere compilata`
        : `Compilare le sezioni: ${validationErrors.join(', ')}`;
      
      enqueueSnackbar(errorMessage, { 
        variant: "error", 
        autoHideDuration: 6000
      });
      return;
    }

    // Debug: stampa i valori dei radio button per gli avvocati
    if (tenantTypeId === 3) {
      console.log("Radio values being saved:", selectedRadioValues);
    }

    // Prepara i dati del rischio aggiornati
    const updatedRischio = {
      ...selectedRischio,
      date: dataValutazione || selectedRischio.date, // Aggiorna con la data di valutazione selezionata
      sp_a1_livello: sliderVal.sliderA1,
      sp_a2_livello: sliderVal.sliderA2,
      sp_a3_livello: sliderVal.sliderA3,
      sp_a4_livello: sliderVal.sliderA4,
      sp_b1_livello: sliderVal.sliderB1,
      sp_b2_livello: sliderVal.sliderB2,
      sp_b3_livello: sliderVal.sliderB3,
      sp_b4_livello: sliderVal.sliderB4,
      sp_b5_livello: sliderVal.sliderB5,
      sp_b6_livello: sliderVal.sliderB6,
      sp_b7_livello: sliderVal.sliderB7,
      sp_c1_1_livello: sliderVal.sliderC1,
      sp_c1_2_livello: sliderVal.sliderC2,
      sp_c1_3_livello: sliderVal.sliderC3,
      // Checkbox values A
      sp_a1_1: chbVal.chbA1_1,
      sp_a1_2: chbVal.chbA1_2,
      sp_a1_3: chbVal.chbA1_3,
      sp_a1_4: chbVal.chbA1_4,
      sp_a1_5: chbVal.chbA1_5,
      sp_a1_6: chbVal.chbA1_6,
      sp_a1_7: chbVal.chbA1_7,
      sp_a1_altro: chbVal.chbA1_Altro,
      sp_a2_1: chbVal.chbA2_1,
      sp_a2_2: chbVal.chbA2_2,
      sp_a2_3: chbVal.chbA2_3,
      sp_a2_altro: chbVal.chbA2_Altro,
      sp_a3_1: chbVal.chbA3_1,
      sp_a3_2: chbVal.chbA3_2,
      sp_a3_3: chbVal.chbA3_3,
      sp_a3_4: chbVal.chbA3_4,
      sp_a3_altro: chbVal.chbA3_Altro,
      sp_a4_1: chbVal.chbA4_1,
      sp_a4_2: chbVal.chbA4_2,
      sp_a4_3: chbVal.chbA4_3,
      sp_a4_4: chbVal.chbA4_4,
      sp_a4_altro: chbVal.chbA4_Altro,
      // Checkbox values B
      sp_b1_1: chbVal.chbB1_1,
      sp_b1_2: chbVal.chbB1_2,
      sp_b1_3: chbVal.chbB1_3,
      sp_b1_4: chbVal.chbB1_4,
      sp_b1_altro: chbVal.chbB1_Altro,
      sp_b2_1: chbVal.chbB2_1,
      sp_b2_2: chbVal.chbB2_2,
      sp_b2_3: chbVal.chbB2_3,
      sp_b2_4: chbVal.chbB2_4,
      sp_b2_altro: chbVal.chbB2_Altro,
      sp_b3_1: chbVal.chbB3_1,
      sp_b3_2: chbVal.chbB3_2,
      sp_b3_3: chbVal.chbB3_3,
      sp_b3_altro: chbVal.chbB3_Altro,
      sp_b4_1: chbVal.chbB4_1,
      sp_b4_2: chbVal.chbB4_2,
      sp_b4_3: chbVal.chbB4_3,
      sp_b4_altro: chbVal.chbB4_Altro,
      sp_b5_1: chbVal.chbB5_1,
      sp_b5_2: chbVal.chbB5_2,
      sp_b5_3: chbVal.chbB5_3,
      sp_b5_4: chbVal.chbB5_4,
      sp_b5_altro: chbVal.chbB5_Altro,
      sp_b6_1: chbVal.chbB6_1,
      sp_b6_2: chbVal.chbB6_2,
      sp_b6_3: chbVal.chbB6_3,
      sp_b6_4: chbVal.chbB6_4,
      sp_b6_altro: chbVal.chbB6_Altro,
      // Text notes for "Altro" fields
      sp_a1_altro_note: textNotes.txtAltroA1,
      sp_a2_altro_note: textNotes.txtAltroA2,
      sp_a3_altro_note: textNotes.txtAltroA3,
      sp_a4_altro_note: textNotes.txtAltroA4,
      sp_b1_altro_note: textNotes.txtAltroB1,
      sp_b2_altro_note: textNotes.txtAltroB2,
      sp_b3_altro_note: textNotes.txtAltroB3,
      sp_b4_altro_note: textNotes.txtAltroB4,
      sp_b5_altro_note: textNotes.txtAltroB5,
      sp_b6_altro_note: textNotes.txtAltroB6,
      // Other values
      hasTabellaB: chbVal.chbTabellaB,
      hasTitEffPep: chbVal.chbHasTitEffPep,
      dataControlloCostante: new Date(dataContCostAdeguataVerifica),
      // Calculated totals
      sp_a_subTotal: subTotaleA,
      sp_b_subTotal: subTotaleB,
      sp_c_subTotal: subTotaleC,
      ef_ri_total: totRischioInerente,
      ef_sp_total: totRischioSpecifico,
      ef_total: totRischioEffettivo,
      ef_adeguata_livello: adeguataVerifica,
    };

    // Chiama la funzione di salvataggio del componente padre
    await onSaveRischio(updatedRischio);
  };

  const handleCancelClick = () => {
    if(!onCancel) return;
    onCancel();
  }
  //#endregion

  //#region Risk Calculation
  const calcoloRischio = () => {
    if (!selectedPrestazione) return;

    // Calcolo sottototali
    const newSubTotaleA = sliderVal.sliderA1 + sliderVal.sliderA2 + sliderVal.sliderA3 + sliderVal.sliderA4;
    let newSubTotaleB = 0;
    if (chbVal.chbTabellaB) {
      newSubTotaleB = sliderVal.sliderB1 + sliderVal.sliderB2 + sliderVal.sliderB3 + sliderVal.sliderB4 + sliderVal.sliderB5 + sliderVal.sliderB6;
      if (tenantTypeId === 2) {
        newSubTotaleB += sliderVal.sliderB7;
      }
    }
    const newSubTotaleC = sliderVal.sliderC1 + sliderVal.sliderC2 + sliderVal.sliderC3;

    setSubTotaleA(newSubTotaleA);
    setSubTotaleB(newSubTotaleB);
    setSubTotaleC(newSubTotaleC);

    // Rischio Inerente (dalla prestazione)
    let totInerente = (selectedPrestazione.prestazioneRischio * 30) / 100;
    if (tenantTypeId === 2) {
      totInerente = (selectedPrestazione.prestazioneRischio * 40) / 100;
    }
    setTotRischioInerente(parseFloat(totInerente.toFixed(2)));

    // Rischio Specifico
    let totale = 0;
    if (chbVal.chbTabellaB) {
      if (tenantTypeId === 3) {
        // Per gli avvocati: 4 elementi A + 6 elementi B = 10 elementi totali
        totale = (newSubTotaleA + newSubTotaleB) / 10;
      } else if (tenantTypeId === 2) {
        // Per i consulenti del lavoro: 4 elementi A + 7 elementi B  + 3 elementi C = 14 elementi totali
        totale = (newSubTotaleA + newSubTotaleB + newSubTotaleC) / 14;
      } else {
        // Per altri: 4 elementi A + 6 elementi B = 10 elementi totali
        totale = (newSubTotaleA + newSubTotaleB) / 10;
      }
    } else {
      if (tenantTypeId === 3) {
        // Per gli avvocati: solo 4 elementi A
        totale = newSubTotaleA / 4;
      } else if (tenantTypeId === 2){
        //Per i consulenti del lavoro: 4 elementi A + 3 elementi C = 7 elementi totali
        totale = (newSubTotaleA + newSubTotaleC) / 7; 
      }
      else {
        // Per altri: solo 4 elementi A
        totale = newSubTotaleA / 4;
      }
    }
    setTotRischioSpecifico(parseFloat(totale.toFixed(2)));

    // Rischio Specifico Ponderato
    let totSpecifico = (totale * 70) / 100;
    if (tenantTypeId === 2) {
      totSpecifico = (totale * 60) / 100;
    }
    setTotRischioSpecificoPonderato(parseFloat(totSpecifico.toFixed(2)));

    // Rischio Effettivo
    const totEffettivo = totInerente + totSpecifico;
    setTotRischioEffettivo(parseFloat(totEffettivo.toFixed(2)));

    // Determinazione livelli e colori
    determineRiskLevels(totInerente, totEffettivo,totSpecifico, newSubTotaleA + newSubTotaleB);
  };

  const determineRiskLevels = (rischioInerente, rischioEffettivo,rischioSpecifico, punteggioComplessivo) => {
    // Determinazione livello rischio inerente basato sulla prestazione
    if (selectedPrestazione) {
      switch (selectedPrestazione.prestazioneRischio) {
        case 1:
          setRischioInerenteLivelloS(t("valutazione.form.rischioInerenteLivello1"));
          setRischioInerenteLivelloC(chipGreen);
          break;
        case 2:
          setRischioInerenteLivelloS(t("valutazione.form.rischioInerenteLivello2"));
          setRischioInerenteLivelloC(chipYellow);
          break;
        case 3:
          setRischioInerenteLivelloS(t("valutazione.form.rischioInerenteLivello3"));
          setRischioInerenteLivelloC(chipOrange);
          break;
        case 4:
          setRischioInerenteLivelloS(t("valutazione.form.rischioInerenteLivello4"));
          setRischioInerenteLivelloC(chipRed);
          break;
        default:
          setRischioInerenteLivelloS(t("valutazione.form.rischioInerenteLivello1"));
          setRischioInerenteLivelloC(chipGreen);
          break;
      }
    }

    // Determinazione livello rischio effettivo
    if (rischioEffettivo >= 1 && rischioEffettivo <= 1.5) {
      setRischioEffettivoLivelloS(t("valutazione.form.rischioInerenteLivello1"));
      setRischioEffettivoLivelloC(chipGreen);
    } else if (rischioEffettivo > 1.5 && rischioEffettivo <= 2.5) {
      setRischioEffettivoLivelloS(t("valutazione.form.rischioInerenteLivello2"));
      setRischioEffettivoLivelloC(chipYellow);
    } else if (rischioEffettivo > 2.5 && rischioEffettivo <= 3.5) {
      setRischioEffettivoLivelloS(t("valutazione.form.rischioInerenteLivello3"));
      setRischioEffettivoLivelloC(chipOrange);
    } else if (rischioEffettivo > 3.5) {
      setRischioEffettivoLivelloS(t("valutazione.form.rischioInerenteLivello4"));
      setRischioEffettivoLivelloC(chipRed);
    }

    //Determinazione livello rischio specifico
    if (rischioSpecifico >= 1 && rischioSpecifico <= 1.5) {
      setRischioSpecificoLivelloS(t("valutazione.form.rischioInerenteLivello1"));
      setRischioSpecificoLivelloC(chipGreen);
    } else if (rischioSpecifico > 1.5 && rischioSpecifico <= 2.5) {
      setRischioSpecificoLivelloS(t("valutazione.form.rischioInerenteLivello2"));
      setRischioSpecificoLivelloC(chipYellow);
    } else if (rischioSpecifico > 2.5 && rischioSpecifico <= 3.5) {
      setRischioSpecificoLivelloS(t("valutazione.form.rischioInerenteLivello3"));
      setRischioSpecificoLivelloC(chipOrange);
    } else if (rischioSpecifico > 3.5) {
      setRischioSpecificoLivelloS(t("valutazione.form.rischioInerenteLivello4"));
      setRischioSpecificoLivelloC(chipRed);
    }

    
    
    // Le regole descrittive restano indipendenti dall'offset di controllo costante.
    
    if (getAdeguateVerificationRules) {
      const rules = getAdeguateVerificationRules(rischioEffettivo, chbVal.chbHasTitEffPep);
      setRules(rules);
    }
    
    if (rischioEffettivo >= 1.0 && rischioEffettivo <= 1.5) {
      setAdeguataVerifica(1);
      setAdeguataVerificaS(t("valutazione.form.adeguataVerificaLivello1"));
      setAdeguataVerificaC(chipGreen);
    } else if (rischioEffettivo > 1.5 && rischioEffettivo <= 2.5) {
      setAdeguataVerifica(2);
      setAdeguataVerificaS(t("valutazione.form.adeguataVerificaLivello1"));
      setAdeguataVerificaC(chipYellow);
    } else if (rischioEffettivo > 2.5 && rischioEffettivo <= 3.5) {
      setAdeguataVerifica(2);
      setAdeguataVerificaS(t("valutazione.form.adeguataVerificaLivello2"));
      setAdeguataVerificaC(chipOrange);
    } else if (rischioEffettivo > 3.5) {
      setAdeguataVerifica(3);
      setAdeguataVerificaS(t("valutazione.form.adeguataVerificaLivello3"));
      setAdeguataVerificaC(chipRed);
    }

    const rischioPerOffset = tenantTypeId === 3 ? punteggioComplessivo : rischioEffettivo;
    setOffsetMesi(getRiskGradeOffset(rischioPerOffset, baseRischioGradoList, tenantTypeId, chbVal.chbTabellaB));

    // PEP forza sempre la verifica rafforzata e i 6 mesi, anche se manca un grado.
    if (chbVal.chbHasTitEffPep) {
      setAdeguataVerifica(3);
      setAdeguataVerificaS(t("valutazione.form.adeguataVerificaLivello3"));
      setAdeguataVerificaC(chipRed);
      setOffsetMesi(6);
    }
  };

  //#endregion

  //#region Effetti

  // Mantieni la data salvata al caricamento; ricalcola solo per nuovi rischi o
  // quando l'utente cambia data di valutazione / intervallo di rischio.
  useEffect(() => {
    const baseDate = dataValutazione || selectedRischio?.date;
    const baseDateTime = baseDate ? new Date(baseDate).getTime() : null;
    const previous = previousControlDateInputs.current;
    const riskId = selectedRischio?.id;
    previousControlDateInputs.current = { riskId, baseDateTime, offsetMesi };

    const sameRisk = previous?.riskId === riskId;
    const evaluationDateChanged = sameRisk && previous.baseDateTime !== baseDateTime;
    const offsetChanged = sameRisk && previous.offsetMesi !== offsetMesi;
    if (offsetMesi !== null && baseDateTime !== null && Number.isFinite(baseDateTime) &&
        (evaluationDateChanged || (offsetChanged && riskInputsChanged.current) ||
          (!dataContCostAdeguataVerifica && (sameRisk || riskId === -1)))) {
      const controlDate = new Date(baseDate);
      controlDate.setMonth(controlDate.getMonth() + offsetMesi);
      setDataContCostAdeguataVerifica(controlDate);
    }
  }, [offsetMesi, dataValutazione, selectedRischio, dataContCostAdeguataVerifica]);

  useEffect(() => {
    calcoloRischio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sliderVal,
    chbVal,
    selectedRischio,
    selectedPrestazione,
    baseRischioGradoList,
    textNotes
  ]);

  //#endregion

  if (!selectedRischio) {
    return (
      <Box sx={{ p: 2 }}>
        <ExtendedTypography variant="h6" color="text.secondary">
          {t("rischio.selectRischio")}
        </ExtendedTypography>
      </Box>
    );
  }

  return (
    <>
        <Stack component="fieldset" disabled={!isUserAllowedToManage} direction="column" spacing={2} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
            {/* Prestazione Details and Controls */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Stack direction="row" spacing={2}>
                <Stack>
                  <ExtendedTypography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
                      Prestazione Selezionata
                  </ExtendedTypography>
                  <ExtendedTypography variant="body2" sx={{ maxWidth: 400 }}>
                      {selectedPrestazione ? selectedPrestazione.prestazioneNome : t("rischio.selezionaPrestazione")}
                  </ExtendedTypography>
                </Stack>
                <Divider orientation="vertical" flexItem />                
                <Stack>
                  <ExtendedTypography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
                      Configurazione Valutazione
                  </ExtendedTypography>
                  <Stack direction="row" spacing={3}> 
                      {/* Tabella B Control */}
                      <Stack direction="row" alignItems="center" spacing={1}>
                          <Checkbox
                              id="chbTabellaB"
                              checked={chbVal.chbTabellaB}
                              onChange={handleCheckboxChange}
                              disabled={!isUserAllowedToManage}
                          />
                          <ExtendedTypography variant="body2">
                              {t("valutazione.form.compilaTabellaB")}
                          </ExtendedTypography>
                      </Stack>

                      {/* T.E. PEP Control */}         
                      <Stack direction="row" alignItems="center" spacing={1}>
                          <Checkbox
                              id="chbHasTitEffPep"
                              checked={chbVal.chbHasTitEffPep}
                              onChange={handleCheckboxChange}
                              disabled={!isUserAllowedToManage || !fascicoloHasTitEfPep}
                          />
                          <ExtendedTypography variant="body2" color={!fascicoloHasTitEfPep ? "text.disabled" : "text.primary"}>
                              Titolare Effettivo Politicamente esposto
                          </ExtendedTypography>
                      </Stack>                    
                  </Stack>
                </Stack>
              </Stack>
            </Paper>

            {/* Main Risk Assessment Form */}
            <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />} sx={{pl:2}}>
                {/* Left Column - Risk Tables */}
                <Stack direction="column" spacing={1} sx={{ width: "70%" }}>
                <ExtendedTypography variant="titleSmall" color="primary.main">
                    {t("valutazione.form.rischioSpecifico")}
                </ExtendedTypography>

                {/* Section A */}
                <RischioSectionA
                    tenantTypeId={tenantTypeId}
                    sliderVal={sliderVal}
                    chbVal={chbVal}
                    textNotes={textNotes}
                    selectedRadioValues={selectedRadioValues}
                    currentRischio={selectedRischio}
                    subTotaleA={subTotaleA}
                    isUserAllowedToManage={isUserAllowedToManage}
                    onSliderChange={handleSliderChange}
                    onCheckboxChange={handleCheckboxChange}
                    onRadioChange={handleRadioChange}
                    onTableRowClick={handleTableRowClick}
                    sliderLivelli={sliderLivelli}
                    sliderLivelliAvv={sliderLivelliAvv}
                    rischioPaeseCliente={rischioPaeseCliente}
                    rischioProvinciaCliente={rischioProvinciaCliente}
                />

                {/* Section B */}                
                <RischioSectionB
                    tenantTypeId={tenantTypeId}
                    sliderVal={sliderVal}
                    chbVal={chbVal}
                    textNotes={textNotes}
                    selectedRadioValues={selectedRadioValues}
                    currentRischio={selectedRischio}
                    subTotaleB={subTotaleB}
                    isUserAllowedToManage={isUserAllowedToManage}
                    onSliderChange={handleSliderChange}
                    onCheckboxChange={handleCheckboxChange}
                    onRadioChange={handleRadioChange}
                    onTableRowClick={handleTableRowClick}
                    sliderLivelli={sliderLivelli}
                    sliderLivelliAvv={sliderLivelliAvv}
                    rischioPaesePrest={rischioPaesePrest}
                    rischioProvinciaPrest={rischioProvinciaPrest}
                    showTabellaB={chbVal.chbTabellaB}
                />
                

                {/* Section C */}
                {tenantTypeId === 2 &&
                    <RischioSectionC
                        tenantTypeId={tenantTypeId}
                        sliderVal={sliderVal}
                        isUserAllowedToManage={isUserAllowedToManage}
                        onSliderChange={handleSliderChange}
                        sliderLivelli={sliderLivelli}
                        sliderLivelliAvv={sliderLivelliAvv}
                        subTotaleC={subTotaleC}
                    />
                }         
                </Stack>
                {/* Right Column - Controls and Info */}
                <Stack direction="column" spacing={1} sx={{ width: "30%" }}>
                    {/* Summary */}
                    <RischioSummary
                        rischioInerenteLivelloS={rischioInerenteLivelloS}
                        rischioInerenteLivelloC={rischioInerenteLivelloC}
                        rischioEffettivoLivelloS={rischioEffettivoLivelloS}
                        rischioEffettivoLivelloC={rischioEffettivoLivelloC}
                        livelloAdeguataVerificaS={adeguataVerificaS}
                        livelloAdeguataVerificaC={adeguataVerificaC}
                        totRischioInerente={totRischioInerente}
                        totRischioSpecifico={totRischioSpecifico}
                        totRischioSpecificoPonderato={totRischioSpecificoPonderato}
                        rischioSpecificoLivelloS={rischioSpecificoLivelloS}
                        rischioSpecificoLivelloC={rischioSpecificoLivelloC}
                        totRischioEffettivo={totRischioEffettivo}
                        chbHasTitEffPep={chbVal.chbHasTitEffPep}
                        currentPrestazione={selectedPrestazione}
                        regoleAdeguataVerifica={rules !== null ? rules.adeguataVerifica : ""}
                        subTotaleA={subTotaleA}
                        subTotaleB={subTotaleB}
                        tenantTypeId={tenantTypeId}
                    />

                    {/* Date Control */}
                    <ExtendedTypography variant="subtitle2">
                        <b>Controllo Costante :</b> {offsetMesi === null ? "—" : `${offsetMesi} mesi`}
                    </ExtendedTypography>
                    <Stack direction="column" spacing={2}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                            <DatePicker
                            label="Prossima verifica"
                            value={dataContCostAdeguataVerifica ? new Date(dataContCostAdeguataVerifica) : null}
                            onChange={handleDateChange}
                            closeOnSelect
                            format="dd/MM/yyyy"
                            slotProps={{
                                textField: {
                                size: "small",
                                fullWidth: true,
                                },
                            }}
                            disabled={!isUserAllowedToManage}
                            />
                        </LocalizationProvider>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
        <VaporToolbar
                variant="regular"
                size="medium"
                withoutAppBar={false}
                contentRight={
                    <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        endIcon={<CancelIcon />}
                        onClick={handleCancelClick}
                    >
                        {t("actions.annulla")}
                    </Button>
                    <Button
                        variant="contained"
                        size="medium"
                        endIcon={<SaveIcon />}
                        onClick={handleSaveRischio}
                        disabled={!isUserAllowedToManage}
                    >
                        {selectedRischio.id === -1
                        ? t("actions.salva")
                        : t("actions.aggiorna")}
                    </Button>
                    </Stack>
                }
        />
    </>
  );
}